import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { mountThanosWidget } from "./mountThanosWidget";
import { syncThanosWidgetEmbed } from "./thanosWidgetEmbed";

export type ReviewOfferWidgetPopupProps = {
  orgId: string;
  userId: string;
  campaignId?: string | number;
  locale?: string;
  onClose: () => void;
  onOpened?: () => void;
};

/**
 * S2 "Pop-up open" (frontend-spec.md). Mounts the Thanos widget (fixture
 * until HUB-455 ships) over the drawer. The offer row underneath is left
 * unchanged per spec.
 */
export const ReviewOfferWidgetPopup = ({
  orgId,
  userId,
  campaignId,
  locale = "en",
  onClose,
  onOpened,
}: ReviewOfferWidgetPopupProps) => {
  const widgetRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    mountThanosWidget();
    onOpened?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The widget hardcodes a light palette and exposes no theming hooks; its
  // shadow root is open, so we style it from here. See thanosWidgetEmbed.ts.
  useEffect(() => syncThanosWidgetEmbed(widgetRef.current), []);

  return createPortal(
    <div
      // Backdrop: dim + blur so the widget reads as a focused modal over the
      // drawer. bg-black/40 alone was too light to separate the two layers.
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/70 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-label="Leave a review"
      data-cypress-id="reviewOfferWidgetOverlay"
    >
      <div className="relative max-h-[90vh] w-full max-w-[460px] overflow-auto rounded-xl bg-white shadow-xl dark:bg-slate-900">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          data-cypress-id="reviewOfferWidgetClose"
        >
          <X className="h-5 w-5" />
        </button>
        <thanos-review-widget
          ref={widgetRef}
          org-id={orgId}
          user-id={userId}
          campaign-id={campaignId != null ? String(campaignId) : undefined}
          locale={locale}
          source="checkout_offer"
          incentivized="true"
          data-cypress-id="reviewOfferThanosWidget"
        />
      </div>
    </div>,
    document.body
  );
};
